import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState('');
  const [location, setLocation] = useState('');

  const handleSearch = (e) => {
    e.preventDefault();
    navigate('/jobs', { state: { keyword, location } });
  };

  const featuredJobs = [
    {
      id: 1,
      title: 'Senior Frontend Architect',
      company: 'Tech Innovations Pvt Ltd',
      location: 'Mumbai, Maharashtra',
      salaryMin: 1400000,
      salaryMax: 2000000,
      badges: ['New', 'Premium'],
      description: 'Lead the design and implementation of scalable frontend architectures. Work with cutting-edge technologies and mentor a team of brilliant engineers.'
    },
    {
      id: 2,
      title: 'Product Manager — AI/ML',
      company: 'StartUp Ventures',
      location: 'Bangalore, Karnataka',
      salaryMin: 1800000,
      salaryMax: 2500000,
      badges: ['Remote'],
      description: 'Shape the future of AI-powered products. Drive strategy, execution, and innovation in a fast-paced environment with global impact.'
    },
    {
      id: 3,
      title: 'Creative Director',
      company: 'Digital Agency Co',
      location: 'Delhi, NCR',
      salaryMin: 1200000,
      salaryMax: 1800000,
      badges: ['New'],
      description: 'Lead creative vision for premium brands. Build compelling narratives that resonate with audiences and drive business outcomes.'
    },
    {
      id: 4,
      title: 'Senior Backend Engineer',
      company: 'Cloud Solutions Inc',
      location: 'Pune, Maharashtra',
      salaryMin: 1600000,
      salaryMax: 2200000,
      badges: ['Premium', 'Remote'],
      description: 'Build robust, scalable backend systems. Work with microservices, cloud infrastructure, and solve complex distributed systems challenges.'
    },
    {
      id: 5,
      title: 'UX Research Lead',
      company: 'Design Studios',
      location: 'Hyderabad, Telangana',
      salaryMin: 1300000,
      salaryMax: 1900000,
      badges: ['New'],
      description: 'Uncover deep user insights through qualitative and quantitative research. Shape product direction with data-driven recommendations.'
    },
    {
      id: 6,
      title: 'DevOps Architect',
      company: 'Infrastructure Labs',
      location: 'Chennai, Tamil Nadu',
      salaryMin: 1700000,
      salaryMax: 2400000,
      badges: ['Remote'],
      description: 'Design and implement CI/CD pipelines, orchestrate cloud infrastructure, and ensure system reliability at scale.'
    }
  ];

  const formatSalary = (min, max) => {
    const formatNum = (num) => {
      const lakh = num / 100000;
      return `₹${lakh.toFixed(0)}L`;
    };
    return `${formatNum(min)} — ${formatNum(max)}`;
  };

  return (
    <div className="home">
      <Navbar />

      {/* Hero Section */}
      <motion.section 
        className="hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="hero-content">
          <div className="hero-text">
            <motion.h1
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
            >
              Where <span className="highlight">Exceptional</span><br />
              Talent Meets<br />
              Elite Opportunities
            </motion.h1>

            <motion.p 
              className="hero-subtitle"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.8 }}
            >
              A curated platform exclusively for ambitious students seeking premium career opportunities. 
              Join our members-only network and gain instant access to India's most coveted positions.
            </motion.p>

            <motion.form 
              className="search-container"
              onSubmit={handleSearch}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.8 }}
            >
              <input
                type="text"
                placeholder="Role or expertise"
                className="search-field"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              <input
                type="text"
                placeholder="Location"
                className="search-field"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
              <button type="submit" className="btn-search">
                Search
              </button>
            </motion.form>

            <motion.div 
              className="hero-stats"
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.8 }}
            >
              <div className="stat-item">
                <div className="stat-number">10K+</div>
                <div className="stat-label">Curated Roles</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">500+</div>
                <div className="stat-label">Elite Partners</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">24h</div>
                <div className="stat-label">Instant Alerts</div>
              </div>
            </motion.div>
          </div>

          <div className="hero-visual">
            <motion.div 
              className="floating-card"
              animate={{ y: [0, -20, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <span className="card-badge">Premium</span>
              <div className="card-title">Senior Product Designer</div>
              <div className="card-company">Tech Innovations</div>
              <div className="card-salary">₹12L - ₹18L</div>
            </motion.div>
            <motion.div 
              className="floating-card"
              style={{ top: '45%', right: '10%', left: 'auto' }}
              animate={{ y: [0, -20, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 2 }}
            >
              <span className="card-badge">Featured</span>
              <div className="card-title">Full Stack Engineer</div>
              <div className="card-company">Digital Ventures</div>
              <div className="card-salary">₹15L - ₹22L</div>
            </motion.div>
            <motion.div 
              className="floating-card"
              style={{ bottom: '10%', left: '15%', top: 'auto' }}
              animate={{ y: [0, -20, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 4 }}
            >
              <span className="card-badge">New</span>
              <div className="card-title">Data Scientist</div>
              <div className="card-company">AI Labs India</div>
              <div className="card-salary">₹18L - ₹25L</div>
            </motion.div>
          </div>
        </div>
      </motion.section>

      {/* Featured Opportunities */}
      <section className="featured" id="opportunities">
        <div className="section-header">
          <h2 className="section-title">Featured Opportunities</h2>
          <p className="section-subtitle">
            Handpicked roles from India's most innovative companies. Updated in real-time.
          </p>
        </div>

        <div className="jobs-grid">
          {featuredJobs.map((job, index) => (
            <motion.div
              key={job.id}
              className="job-card"
              initial={{ y: 50, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
            >
              <div className="job-header">
                <div className="job-badges">
                  {job.badges.includes('New') && <span className="badge badge-new">New</span>}
                  {job.badges.includes('Premium') && <span className="badge badge-premium">Premium</span>}
                  {job.badges.includes('Remote') && <span className="badge badge-remote">Remote</span>}
                </div>
              </div>
              <h3 className="job-title">{job.title}</h3>
              <div className="job-company">⚡ {job.company}</div>
              <div className="job-location">📍 {job.location}</div>
              <div className="job-salary">{formatSalary(job.salaryMin, job.salaryMax)}</div>
              <p className="job-description">{job.description}</p>
              <div className="job-actions">
                <button className="btn-apply" onClick={() => alert('View Details coming soon!')}>
                  View Details
                </button>
                <button className="btn-save" onClick={(e) => {
                  e.target.textContent = e.target.textContent === '♡' ? '♥' : '♡';
                }}>
                  ♡
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2 className="cta-title">Ready to Elevate<br />Your Career?</h2>
          <p className="cta-text">
            Join thousands of ambitious professionals who've found their dream roles through J. 
            Get instant access to exclusive opportunities.
          </p>
          <button className="btn-large" onClick={() => alert('Membership coming soon!')}>
            Become a Member
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
